/**
 * Sentry Error Tracking Configuration
 *
 * Industry best practices for error tracking:
 * - Only initialize in production
 * - Sanitize sensitive data before sending
 * - Configure appropriate sample rates
 * - Set up release tracking
 */

import * as Sentry from "@sentry/react";
import { getEnvValue, getRuntimeMode } from "./env";

/**
 * Initialize Sentry for error tracking
 * Only initializes in production environment
 */
export function initSentry(): void {
  // Only initialize in production
  const mode = getRuntimeMode();
  const isProduction = mode === "production" || getEnvValue("PROD") === "true";

  if (!isProduction) {
    // In development, log that Sentry is disabled
    return;
  }

  // Get DSN from environment variable
  const dsn = getEnvValue("VITE_SENTRY_DSN");

  if (!dsn) {
    return;
  }

  try {
    Sentry.init({
      dsn,
      integrations: [Sentry.browserTracingIntegration()],

      // Performance monitoring sample rate (10% of transactions)
      tracesSampleRate: 0.1,

      // Set sample rate for error events (100% in production)
      sampleRate: 1.0,

      // Environment
      environment: mode || "production",

      // Release tracking (set via CI/CD)
      release:
        getEnvValue("VITE_APP_VERSION"),

      // Before sending event, sanitize sensitive data
      beforeSend(event) {
        // Sanitize sensitive data from event
        if (event.request) {
          // Remove sensitive headers
          if (event.request.headers) {
            delete event.request.headers["Authorization"];
            delete event.request.headers["authorization"];
            delete event.request.headers["Cookie"];
            delete event.request.headers["cookie"];
          }

          // Sanitize URL parameters
          if (event.request.url) {
            event.request.url = sanitizeURL(event.request.url);
          }
        }

        // Sanitize user data
        if (event.user) {
          // Only keep non-sensitive user data
          event.user = {
            id: event.user.id,
            // Don't send email or other PII
          };
        }

        // Sanitize breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((crumb) => {
            if (crumb.data) {
              // Remove sensitive data from breadcrumbs
              const sanitizedData = { ...crumb.data };
              Object.keys(sanitizedData).forEach((key) => {
                const lowerKey = key.toLowerCase();
                if (
                  lowerKey.includes("token") ||
                  lowerKey.includes("password") ||
                  lowerKey.includes("secret") ||
                  lowerKey.includes("auth")
                ) {
                  sanitizedData[key] = "[REDACTED]";
                }
              });
              return { ...crumb, data: sanitizedData };
            }
            return crumb;
          });
        }

        return event;
      },

      // Ignore certain errors (e.g., browser extensions, network errors)
      ignoreErrors: [
        // Browser extensions
        "top.GLOBALS",
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "atomicFindClose",
        "fb_xd_fragment",
        "bmi_SafeAddOnload",
        "EBCallBackMessageReceived",
        "conduitPage",
        // Network errors (often not actionable)
        "NetworkError",
        "Network request failed",
        "Failed to fetch",
        // ResizeObserver errors (common, not actionable)
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
      ],

      // Don't send errors from browser extensions
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
      ],
    });

  } catch {
    // Error reporting must never prevent the application from loading.
  }
}

/**
 * Sanitize URL to remove sensitive query parameters
 */
function sanitizeURL(url: string): string {
  try {
    const urlObj = new URL(url);
    const sensitiveParams = [
      "token",
      "password",
      "secret",
      "key",
      "auth",
      "api_key",
      "access_token",
    ];

    sensitiveParams.forEach((param) => {
      urlObj.searchParams.delete(param);
    });

    return urlObj.toString();
  } catch {
    // If URL parsing fails, return original
    return url;
  }
}

/**
 * Set user context for error tracking
 * Call this after user login
 */
export function setSentryUser(
  user: { id: number | string; email?: string } | null
): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  // Only set non-sensitive user data
  Sentry.setUser({
    id: String(user.id),
    // Don't set email or other PII
  });
}

/**
 * Capture exception manually
 * Use this for non-React errors or custom error handling
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture message manually
 * Use this for important events that aren't errors
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info"
): void {
  Sentry.captureMessage(message, level);
}
